import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { hotelService } from '@/services/hotel.service';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { StripeCheckoutDialog } from '@/components/cart/StripeCheckoutDialog';
import { formatApiError } from '@/lib/api';
import { CalendarDays, Users, Minus, Plus, Loader2, CreditCard, ShieldCheck } from 'lucide-react';

const euro = (n) => `€${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const toISO = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const fmtShort = (d) => (d ? d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }) : '');

// converte una stringa ISO (YYYY-MM-DD) in Date a mezzanotte locale
const fromISO = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

function Stepper({ label, hint, value, min, max, onChange, testid }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      <div className="flex items-center gap-3">
        <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full"
          disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))}
          data-testid={`${testid}-minus`} aria-label={`Diminuisci ${label}`}>
          <Minus className="h-4 w-4" />
        </Button>
        <span className="w-5 text-center text-sm font-semibold" data-testid={`${testid}-value`}>{value}</span>
        <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full"
          disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))}
          data-testid={`${testid}-plus`} aria-label={`Aumenta ${label}`}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Box prenotazione della singola camera con pagamento Stripe REALE.
 * - Seleziona date/ospiti → aggiunge la camera al carrello (hold 15') → apre il Payment Element.
 * - Richiede login cliente (come tutto il carrello): se non loggato reindirizza al login e torna qui.
 */
export function RoomBookingBox({ room }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { addStay, refresh } = useCart() || {};
  const isCustomer = user?.role === 'customer';

  const [range, setRange] = useState(undefined);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [dateOpen, setDateOpen] = useState(false);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [occupied, setOccupied] = useState([]); // [{from: Date, to: Date}] notti gia' occupate

  // Carica le date occupate (prenotazioni + blocchi + hold di altri) per il calendario
  useEffect(() => {
    let alive = true;
    if (!room?.id || !room?.owner_id) { setOccupied([]); return; }
    (async () => {
      try {
        const res = await hotelService.publicRoomOccupied(room.id, room.owner_id, 8);
        if (!alive) return;
        const ranges = (res?.ranges || [])
          .map((r) => {
            const from = fromISO(r.from);
            const toExclusive = fromISO(r.to);
            if (!from || !toExclusive) return null;
            // la notte occupata e' [from, to): disabilito fino a (to - 1 giorno)
            const to = addDays(toExclusive, -1);
            if (to < from) return null;
            return { from, to };
          })
          .filter(Boolean);
        setOccupied(ranges);
      } catch (_) {
        if (alive) setOccupied([]);
      }
    })();
    return () => { alive = false; };
  }, [room?.id, room?.owner_id]);

  // Matcher per il componente Calendar: passato (before) + notti occupate
  const disabledDays = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return [{ before: today }, ...occupied];
  }, [occupied]);

  const nights = useMemo(() => {
    if (range?.from && range?.to) return Math.max(0, Math.round((range.to - range.from) / 86400000));
    return 0;
  }, [range]);

  const mode = room?.booking_mode || 'camera';
  const wholePrice = Number(room?.whole_price || 0);
  const [scope, setScope] = useState(mode === 'intero' ? 'intero' : 'camera');
  useEffect(() => { setScope(mode === 'intero' ? 'intero' : 'camera'); }, [mode]);
  const effScope = mode === 'camera' ? 'camera' : mode === 'intero' ? 'intero' : scope;

  const perNight = effScope === 'intero' && wholePrice > 0 ? wholePrice : Number(room?.base_price || 0);
  const estTotal = perNight * (nights || 0);
  const maxGuests = Number(room?.capacity_total || 6) || 6;

  const startBooking = async () => {
    if (!(range?.from && range?.to) || nights < 1) {
      toast.error('Seleziona le date di arrivo e partenza');
      setDateOpen(true);
      return;
    }
    if (!room?.owner_id) {
      toast.error('Questa camera non è prenotabile online al momento.');
      return;
    }
    if (!isCustomer) {
      toast.info('Accedi come cliente per prenotare e pagare');
      navigate(`/cliente/accedi?next=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }
    setAdding(true);
    try {
      await addStay({
        owner_id: room.owner_id, room_id: room.id,
        check_in: toISO(range.from), check_out: toISO(range.to),
        adults, children, scope: effScope,
      });
      setPayOpen(true);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || 'Impossibile avviare la prenotazione. Riprova.');
    } finally {
      setAdding(false);
    }
  };

  const onPaid = async (orderId) => {
    setPayOpen(false);
    try { await refresh?.(0); } catch (_) { /* noop */ }
    toast.success('Pagamento completato! Prenotazione confermata.');
    navigate(`/cliente/ordine-confermato/${orderId}`);
  };

  return (
    <div className="mt-4 space-y-3" data-testid="room-booking-box">
      {/* Modalità: camera singola o intero alloggio */}
      {mode === 'entrambi' && (
        <div data-testid="room-booking-scope">
          <Label className="mb-1.5 block text-xs font-medium text-muted-foreground">Cosa vuoi prenotare</Label>
          <div className="grid grid-cols-2 gap-2">
            {[{ v: 'camera', l: 'Camera singola' }, { v: 'intero', l: 'Intero alloggio' }].map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => setScope(o.v)}
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${effScope === o.v ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}
                data-testid={`room-scope-${o.v}`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>
      )}
      {mode === 'intero' && (
        <p className="rounded-lg bg-primary/5 px-3 py-2 text-xs text-muted-foreground" data-testid="room-booking-whole-note">Questo alloggio si prenota per intero.</p>
      )}

      {/* Date */}
      <div>
        <Label className="mb-1.5 block text-xs font-medium text-muted-foreground">Date del soggiorno</Label>
        <Popover open={dateOpen} onOpenChange={setDateOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start gap-2 font-normal" data-testid="room-booking-dates-trigger">
              <CalendarDays className="h-4 w-4 text-primary" />
              {range?.from && range?.to
                ? <span>{fmtShort(range.from)} → {fmtShort(range.to)} · {nights} nott{nights === 1 ? 'e' : 'i'}</span>
                : <span className="text-muted-foreground">Arrivo — Partenza</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={1} disabled={disabledDays} data-testid="room-booking-calendar" />
            <div className="flex items-center justify-between gap-2 border-t border-border p-2">
              <span className="flex items-center gap-1.5 pl-1 text-[11px] text-muted-foreground">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-muted-foreground/30" aria-hidden="true" />
                Date barrate = non disponibili
              </span>
              <Button size="sm" onClick={() => setDateOpen(false)} data-testid="room-booking-dates-done">Fatto</Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Ospiti */}
      <div>
        <Label className="mb-1.5 block text-xs font-medium text-muted-foreground">Ospiti</Label>
        <Popover open={guestsOpen} onOpenChange={setGuestsOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start gap-2 font-normal" data-testid="room-booking-guests-trigger">
              <Users className="h-4 w-4 text-primary" />{adults + children} ospit{adults + children === 1 ? 'e' : 'i'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64" align="start">
            <Stepper label="Adulti" value={adults} min={1} max={maxGuests} onChange={setAdults} testid="room-booking-adults" />
            <Stepper label="Bambini" hint="0-12 anni" value={children} min={0} max={Math.max(0, maxGuests - 1)} onChange={setChildren} testid="room-booking-children" />
          </PopoverContent>
        </Popover>
      </div>

      {nights >= 1 && (
        <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 text-sm" data-testid="room-booking-estimate">
          <span className="text-muted-foreground">{effScope === 'intero' ? 'Intero alloggio · ' : ''}{euro(perNight)} × {nights} nott{nights === 1 ? 'e' : 'i'}</span>
          <span className="font-semibold text-foreground">{euro(estTotal)}</span>
        </div>
      )}

      <Button className="w-full gap-2" size="lg" onClick={startBooking} disabled={adding} data-testid="room-booking-pay">
        {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
        Prenota e paga
      </Button>
      <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5" /> Pagamento sicuro con carta · Stripe
      </p>

      <StripeCheckoutDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        totalLabel={euro(estTotal)}
        onSuccess={onPaid}
      />
    </div>
  );
}

export default RoomBookingBox;
